import React, { useState } from 'react';
import { Send, MessageCircle, X } from 'lucide-react';

interface SponsorCheckInProps {
    isOpen: boolean;
    onClose: () => void;
}

export const SponsorCheckIn: React.FC<SponsorCheckInProps> = ({ isOpen, onClose }) => {
  const [sent, setSent] = useState(false);
  const [note, setNote] = useState('');
  const [emotion, setEmotion] = useState('Serene');
  const [urge, setUrge] = useState('1 - None');

  const handleSend = (e: React.FormEvent) => {
    e.preventDefault();
    setSent(true);
    // In a real app, this would send data to Firestore/Backend
    setTimeout(() => {
        setSent(false);
        setNote('');
        onClose();
    }, 2000);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose}></div>
        
        {/* Modal */}
        <div className="relative w-full max-w-md bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-6 border border-slate-200 dark:border-slate-700 animate-in fade-in zoom-in duration-200">
            <button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
                <X className="w-5 h-5" />
            </button>

            <h3 className="text-xl font-bold dark:text-white mb-2 flex items-center gap-2">
                <MessageCircle className="w-6 h-6 text-brand-500" /> Daily Sponsor Check-in
            </h3>
            <p className="text-sm text-slate-500 mb-6">Send a quick update to keep your sponsor in the loop.</p>
            
            {sent ? (
                <div className="bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 p-8 rounded-xl text-center">
                    <div className="flex justify-center mb-2">
                        <Send className="w-10 h-10" />
                    </div>
                    <p className="font-bold text-lg">Sent!</p>
                    <p className="text-sm opacity-80">Keeping the connection strong.</p>
                </div>
            ) : (
                <form onSubmit={handleSend} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="text-xs font-bold text-slate-500 dark:text-slate-400 block mb-1 uppercase tracking-wider">Emotional State</label>
                        <select 
                            value={emotion}
                            onChange={(e) => setEmotion(e.target.value)}
                            className="w-full text-sm p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none"
                        >
                            <option>Serene</option>
                            <option>Happy</option>
                            <option>Anxious</option>
                            <option>Angry</option>
                            <option>Lonely</option>
                            <option>Tired</option>
                            <option>Sad</option>
                        </select>
                    </div>
                    <div>
                        <label className="text-xs font-bold text-slate-500 dark:text-slate-400 block mb-1 uppercase tracking-wider">Urge Level (1-10)</label>
                        <select 
                            value={urge}
                            onChange={(e) => setUrge(e.target.value)}
                            className="w-full text-sm p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none"
                        >
                            <option>1 - None</option>
                            <option>2 - Very Low</option>
                            <option>3 - Low</option>
                            <option>4 - Noticeable</option>
                            <option>5 - Moderate</option>
                            <option>6 - Uncomfortable</option>
                            <option>7 - Distracting</option>
                            <option>8 - High</option>
                            <option>9 - Very High</option>
                            <option>10 - Emergency</option>
                        </select>
                    </div>
                </div>

                <div>
                    <label className="text-xs font-bold text-slate-500 dark:text-slate-400 block mb-1 uppercase tracking-wider">Additional Notes</label>
                    <textarea 
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="Anything specific to share today? (Optional)"
                        className="w-full h-24 p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none resize-none text-sm"
                    />
                </div>

                <div className="pt-2">
                    <button type="submit" className="w-full bg-brand-600 hover:bg-brand-700 text-white px-4 py-3 rounded-xl font-bold flex items-center justify-center gap-2 transition-all shadow-lg shadow-brand-500/20">
                        <Send className="w-4 h-4" /> Send Update
                    </button>
                </div>
                </form>
            )}
        </div>
    </div>
  );
};
